import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  DataGridPro,
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
  GridSortModel,
  MuiEvent,
} from '@mui/x-data-grid-pro';
import { useDebounce } from '@uidotdev/usehooks';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';

import { SortModel } from '@/data/models/sort-model.model';
import { TripLegSearchMenu } from '@/data/requests/trip-legs';
import { TripLeg, TripLegAddress } from '@/data/models/reservation.model';
import { TripLegEventType } from '@/data/events/trip-leg';
import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_OPTIONS,
} from '@/data/constants';

import * as lookupSlice from '@/data/lookup.slice';
import {
  fetchTripManagementDiscussTripsAsync,
  removeTripLegEventFromQueue,
  selectLoadingTripManagementDiscussTrips,
  selectTripLegEventQueue,
  selectTripManagementDiscussTrips,
} from '@/data/slices/trip-leg.slice';

import dateUtils from '@/services/DateUtils';

import DiscussionDialog from './DiscussionDialog';
import { ButtonException, CellActions } from '@/components/tables/DataGridPro';
import TextSearch from '@/components/controls/TextSearch';
import { selectLoggedInUserInfo } from '@/data/user.slice';

const PREFIX = 'Discussions';

const classes = {
  container: `${PREFIX}-container`,
  grid: `${PREFIX}-grid`,
  actions: `${PREFIX}-actions`,
  filter: `${PREFIX}-filter`,
  subitem: `${PREFIX}-subitem`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(3), // Adds bottom margin to all child components except the last one
    },
    boxShadow: '0px 0px 10px #dfdfdf',
  },

  [`& .${classes.grid}`]: {
    borderStyle: 'none',
    '& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeaderTitleContainerContent, & .MuiDataGrid-columnHeaderTitleContainerContent .MuiTypography-root':
      {
        color: theme.palette.lightTextPrimary.main,
        fontFamily: 'Source Sans Pro',
        fontSize: 14,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 24,
        letterSpacing: 0.17,
      },
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
    '& .MuiDataGrid-cell:first-of-type': {
      padding: 0,
    },
  },
  [`& .${classes.actions}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
  [`& .${classes.filter}`]: {
    color: theme.palette.text.secondary,
    borderWidth: 0,
  },
  [`& .${classes.subitem}`]: {
    fontFamily: 'Source Sans Pro',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '143%',
    letterSpacing: '0.17px',
    color: 'rgba(1, 16, 18, 0.6)',
  },
}));

const DEFAULT_SORT_MODEL: SortModel = {
  column: 'pickUpDueTimeUTC',
  direction: 'asc',
};

export default function Discussions() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const activeSearchRequestRef = useRef<AbortController | null>(null);
  const hasFetchedInitialDataRef = useRef(false);

  const loggedInUserInfo = useAppSelector(selectLoggedInUserInfo);
  const discussionTripLegs = useAppSelector(selectTripManagementDiscussTrips);
  const loading = useAppSelector(selectLoadingTripManagementDiscussTrips);
  const tripLegEventQueue = useAppSelector(selectTripLegEventQueue);

  const [discussionTripLeg, setDiscussionTripLeg] = useState<TripLeg>();
  const [discussionDialogOpen, setDiscussionDialogOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [sortModel, setSortModel] = useState<SortModel>(DEFAULT_SORT_MODEL);

  const [refetchTripLegs, setRefetchTripLegs] = useState(false);
  const debouncedRefetchTripLegs = useDebounce(refetchTripLegs, 200);

  const fetchDiscussionTripLegs = useCallback(() => {
    activeSearchRequestRef.current?.abort();
    const abortController = new AbortController();
    activeSearchRequestRef.current = abortController;

    const syncIdsOnly = hasFetchedInitialDataRef.current;
    hasFetchedInitialDataRef.current = true;

    const userCategoryTypeBC = loggedInUserInfo?.userCategoryTypeBC;
    const clientBC = loggedInUserInfo?.ids?.clientBC as string | undefined;

    dispatch(
      fetchTripManagementDiscussTripsAsync({
        menu: TripLegSearchMenu.DISCUSS,
        search: searchText || undefined,
        page,
        pageSize,
        sort: sortModel.column,
        sortOrder: sortModel.direction,
        clientBC: userCategoryTypeBC === 'CLIENT' ? clientBC : undefined,
        syncIdsOnly,
        signal: abortController.signal,
      }),
    );
  }, [dispatch, page, pageSize, sortModel, searchText, loggedInUserInfo]);

  const onSearchChange = (val: string) => {
    setSearchText(val);
    setPage(1);
  };

  const onPaginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== discussionTripLegs.currentPage - 1) {
      setPage(model.page + 1);
    }
    if (model.pageSize !== pageSize) {
      setPageSize(model.pageSize);
    }
  };

  const onSortModelChange = (model: GridSortModel) => {
    let newModel: SortModel | null = null;
    if (model?.length) {
      newModel = {
        column: model[0].field,
        direction: model[0].sort,
      };
    }

    setPage(1);
    if (newModel) {
      setSortModel(newModel);
    } else {
      setSortModel(DEFAULT_SORT_MODEL);
    }
  };

  // General refetch of page for pagination, sort, search, and filter changes.
  useEffect(() => {
    setRefetchTripLegs(true);
  }, [fetchDiscussionTripLegs, page, pageSize, sortModel, searchText]);

  // Real-time updates
  // TODO: This refetches on any TripLegUnderDiscussion/Cleared event
  // anywhere, with no check for whether it's relevant to the page/filters
  // currently being viewed -- the same bug Monitoring > Exceptions had
  // (see MonitoringExceptions.tsx's realtime effect for the fix: an
  // on/off-page relevance check before refetching, plus a local removal
  // fast path for clears). Apply the same pattern here, ideally via a
  // shared hook rather than copy-pasting it a third time.
  useEffect(() => {
    if (!tripLegEventQueue?.length) return;

    const eventTypesToRefetchOn = [
      TripLegEventType.TripLegUnderDiscussion,
      TripLegEventType.TripLegUnderDiscussionCleared,
    ];
    const shouldRefresh = tripLegEventQueue.some((event) => {
      const detailType = event.detailType as TripLegEventType;
      return eventTypesToRefetchOn.includes(detailType);
    });

    if (shouldRefresh) {
      setRefetchTripLegs(true);
    }
    dispatch(removeTripLegEventFromQueue(tripLegEventQueue.length));
  }, [dispatch, tripLegEventQueue]);

  useEffect(() => {
    if (debouncedRefetchTripLegs) {
      fetchDiscussionTripLegs();
      setRefetchTripLegs(false);
    }
  }, [debouncedRefetchTripLegs]);

  useEffect(() => {
    dispatch(lookupSlice.fetchLookupItemsAsync('tripLegAdjustmentType'));
  }, []);

  useEffect(() => {
    return () => {
      activeSearchRequestRef.current?.abort();
    };
  }, []);

  const handleDiscuss = (evt: any, row: any) => {
    setDiscussionTripLeg(row);
    setDiscussionDialogOpen(true);
    evt.stopPropagation();
  };

  const closeDiscussionDialog = () => {
    setDiscussionDialogOpen(false);
  };

  const viewTrip = (
    params: GridRowParams,
    _evt: MuiEvent<React.MouseEvent>,
    _details: GridCallbackDetails,
  ) => {
    const trip = params.row;
    const url = `/passengers/${trip.passengerUUID}/reservations/${trip.reservationReferenceID}?tripLegID=${trip.tripLegReferenceID}`;
    navigate(url);
  };

  const renderPickupTime = (params: GridCellParams): React.ReactElement => {
    const time = params.row.isWillCall
      ? `${dateUtils.formatAppointmentDate(params.row.appointmentDate, params.row.pickUpAddressTimeZoneIANA)} ${t('discussion.willCall')}`
      : dateUtils.formatPickupDateTime(
          params.row.pickUpDueTimeUTC,
          params.row.pickUpAddressTimeZoneIANA,
        );
    return (
      <Box>
        <Typography>{time}</Typography>
        <Typography className={classes.subitem}>
          {params.row.tripLegReferenceID}
        </Typography>
      </Box>
    );
  };

  const renderAddress = (address: TripLegAddress) => {
    return (
      <Box>
        <Typography>{`${address.addressLine1}`}</Typography>
        <Typography
          className={
            classes.subitem
          }>{`${address.city}, ${address.stateCode}, ${address.zipCode.substring(0, 5)}`}</Typography>
      </Box>
    );
  };

  const renderPickupAddress = (params: GridCellParams): React.ReactElement => {
    const tripLeg = params.row;
    const address = {
      addressLine1: tripLeg.pickUpAddressLine1,
      city: tripLeg.pickUpCity,
      stateCode: tripLeg.pickUpStateCode,
      zipCode: tripLeg.pickUpZipCode,
    };
    return renderAddress(address);
  };

  const renderDropoffAddress = (params: GridCellParams): React.ReactElement => {
    const tripLeg = params.row;
    const address = {
      addressLine1: tripLeg.dropOffAddressLine1,
      city: tripLeg.dropOffCity,
      stateCode: tripLeg.dropOffStateCode,
      zipCode: tripLeg.dropOffZipCode,
    };
    return renderAddress(address);
  };

  const renderMember = (params: GridCellParams): React.ReactElement => {
    const passenger = params.row;
    return (
      <Box>
        <Typography>{`${passenger.passengerFirstName} ${passenger.passengerLastName}`}</Typography>
        <Typography
          className={
            classes.subitem
          }>{`${passenger.clientPassengerID}`}</Typography>
      </Box>
    );
  };

  const renderProvider = (params: GridCellParams): React.ReactElement => {
    const provider = params.row;
    const driver = params.row;
    return (
      <Box>
        <Typography>{`${provider?.providerName || ''}`}</Typography>
        <Typography className={classes.subitem}>
          {`${driver?.driverName || ''}`}
        </Typography>
      </Box>
    );
  };

  const renderException = (params: GridCellParams): React.ReactElement => {
    return <Typography>{t('discussion.underDiscussion')}</Typography>;
  };

  const gridSortModel: GridSortModel = useMemo(() => {
    if (!sortModel?.column) return [];

    const fields = sortModel.column.split(',');
    const directions = sortModel.direction?.split(',') ?? [];
    const index = fields.indexOf('pickUpDueTimeUTC');
    const field = index >= 0 ? fields[index] : fields[0];
    const sort = (index >= 0 ? directions[index] : directions[0]) as
      | 'asc'
      | 'desc'
      | undefined;

    return field ? [{ field, sort }] : [];
  }, [sortModel]);

  const columns: GridColDef[] = [
    {
      field: 'pickUpDueTimeUTC',
      headerName: t('discussion.pickup'),
      width: 180,
      sortable: true,
      renderCell: renderPickupTime,
    },
    {
      field: 'pickupAddress',
      headerName: t('discussion.pickupAddress'),
      flex: 1,
      minWidth: 240,
      sortable: false,
      renderCell: renderPickupAddress,
    },
    {
      field: 'dropoffAddress',
      headerName: t('discussion.dropoffAddress'),
      flex: 1,
      minWidth: 240,
      sortable: false,
      renderCell: renderDropoffAddress,
    },
    {
      field: 'member',
      headerName: t('discussion.member'),
      minWidth: 240,
      sortable: false,
      renderCell: renderMember,
    },
    {
      field: 'providerName',
      headerName: t('discussion.provider'),
      minWidth: 240,
      sortable: false,
      renderCell: renderProvider,
    },
    {
      field: 'exception',
      headerName: t('discussion.exception'),
      minWidth: 140,
      sortable: false,
      renderCell: renderException,
    },
    {
      field: 'actions',
      headerName: '',
      width: 133,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <CellActions>
          <ButtonException
            onClick={(evt) => handleDiscuss(evt, params.row)}
            label={t('discussion.discuss')}
            data-fs-element="DiscussTripStartButton"
          />
        </CellActions>
      ),
    },
  ];

  return (
    <StyledBox className={classes.container}>
      <Typography variant="h2">{t('discussion.discuss')}</Typography>
      <Box className={classes.actions}>
        <TextSearch
          id="text-search"
          icon={<SearchIcon />}
          label={t('discussion.search')}
          placeholder={t('discussion.searchPlaceholder')}
          currentSearchFilter={searchText}
          onSearchChange={onSearchChange}></TextSearch>
      </Box>
      <DataGridPro
        getRowId={(row) => `${row.tripLegReferenceID}`}
        rows={discussionTripLegs.items}
        rowCount={discussionTripLegs.itemCount}
        columns={columns}
        pagination={true}
        paginationModel={{ page: page - 1, pageSize }}
        paginationMode="server"
        sortModel={gridSortModel}
        pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
        onPaginationModelChange={onPaginationChange}
        onSortModelChange={onSortModelChange}
        onRowClick={viewTrip}
        autoHeight={true}
        loading={loading}
        className={classes.grid}
        disableColumnSelector={true}
        hideFooter={false}
        sortingMode="server"
      />
      {discussionTripLeg && (
        <DiscussionDialog
          tripLegReferenceID={discussionTripLeg.tripLegReferenceID!}
          tripLegID={discussionTripLeg.tripLegID!}
          passengerUUID={discussionTripLeg.passengerUUID}
          open={discussionDialogOpen}
          close={closeDiscussionDialog}
        />
      )}
    </StyledBox>
  );
}
